import { isValidPostalCode } from "@/features/geo/shared/postal-codes";
import type { BusinessTask, PrivatTask, State } from "@/features/quote/shared/quote-config";
import { isValidSquareMeters } from "@/features/quote/shared/validation";

export type PrivatStepId =
  | "task"
  | "subtype"
  | "address"
  | "property"
  | "sqm"
  | "frequency"
  | "start"
  | "timing"
  | "preferences"
  | "priority"
  | "postal"
  | "contact";

export type BusinessStepId =
  | "business"
  | "task"
  | "subtype"
  | "address"
  | "sqm"
  | "frequency"
  | "start"
  | "timing"
  | "preferences"
  | "priority"
  | "postal"
  | "contact";

export type StepId = "audience" | PrivatStepId | BusinessStepId;

function getPrivatSteps(task: PrivatTask | null, addressLookupEnabled: boolean): PrivatStepId[] {
  const steps: PrivatStepId[] = ["task"];
  if (task === "engangs") steps.push("subtype");
  if (addressLookupEnabled) steps.push("address");
  steps.push("property", "sqm");
  if (task === "lobende") steps.push("frequency");
  steps.push("start", "timing", "preferences", "priority", "postal", "contact");
  return steps;
}

function getBusinessSteps(
  task: BusinessTask | null,
  addressLookupEnabled: boolean,
): BusinessStepId[] {
  const steps: BusinessStepId[] = ["business", "task"];
  if (task === "engangs") steps.push("subtype");
  if (addressLookupEnabled) steps.push("address");
  steps.push("sqm");
  if (task === "lobende") steps.push("frequency");
  steps.push("start", "timing", "preferences", "priority", "postal", "contact");
  return steps;
}

// One place for each step's phase label and ready predicate. The ordering
// functions (getPrivatSteps/getBusinessSteps) decide the sequence; this map
// decides when a step is done and which phase it belongs to, shared by privat
// and erhverv.
// `ready` is a pure function of state and branches on audience itself where needed.
export const STEP_META: Record<StepId, { phase: string; ready: (s: State) => boolean }> = {
  audience: { phase: "Kom i gang", ready: (s) => s.audience !== null },
  business: { phase: "Opgaven", ready: (s) => s.business !== null },
  task: {
    phase: "Opgaven",
    ready: (s) => (s.audience === "erhverv" ? s.businessTask !== null : s.task !== null),
  },
  subtype: {
    phase: "Opgaven",
    ready: (s) =>
      s.audience === "erhverv"
        ? s.engangsSubtype !== "" &&
          (s.engangsSubtype !== "andet" || s.engangsAndetDescription.trim() !== "")
        : s.engangsSubtype !== "",
  },
  address: { phase: "Adresse", ready: (s) => s.address !== null || s.addressSkipped },
  property: {
    phase: "Detaljer",
    ready: (s) => s.propertyType !== "" && (s.propertyType !== "lejlighed" || s.floor !== ""),
  },
  sqm: { phase: "Detaljer", ready: (s) => isValidSquareMeters(s.squareMeters) },
  frequency: { phase: "Detaljer", ready: (s) => s.frequency !== "" },
  start: {
    phase: "Detaljer",
    ready: (s) => s.startTiming !== "" && (s.startTiming !== "SPECIFIK_DATO" || s.startDate !== ""),
  },
  timing: { phase: "Detaljer", ready: (s) => s.timeSlots.length > 0 },
  preferences: { phase: "Detaljer", ready: () => true },
  priority: { phase: "Detaljer", ready: (s) => s.priority !== "" },
  postal: { phase: "Detaljer", ready: (s) => isValidPostalCode(s.postalCode) },
  contact: {
    phase: "Kontakt",
    ready: (s) =>
      s.contactName.trim() !== "" &&
      s.email.trim() !== "" &&
      s.phone.trim() !== "" &&
      s.consent &&
      (s.audience !== "erhverv" || s.companyName.trim() !== ""),
  },
};

export function phaseLabelFor(id: StepId | null): string | undefined {
  return id ? STEP_META[id].phase : undefined;
}

// Before an audience is chosen the privat sequence stands in, so the progress
// bar has a length.
export function wizardSteps(
  state: State,
  addressLookupEnabled: boolean,
  audienceLocked: boolean,
): StepId[] {
  const own: StepId[] =
    state.audience === "erhverv"
      ? getBusinessSteps(state.businessTask, addressLookupEnabled)
      : getPrivatSteps(state.task, addressLookupEnabled);
  return audienceLocked ? own : ["audience", ...own];
}

// 1-based like `step` in QuoteWizard. Preferences are optional, so they only
// count as answered when the link named some — otherwise the customer would
// never see the question.
export function initialStepFor(
  state: State,
  addressLookupEnabled: boolean,
  audienceLocked: boolean,
): number {
  const steps = wizardSteps(state, addressLookupEnabled, audienceLocked);
  const firstOpen = steps.findIndex((id) => !isAnswered(id, state));
  return (firstOpen === -1 ? steps.length - 1 : firstOpen) + 1;
}

function isAnswered(id: StepId, state: State): boolean {
  if (id === "preferences") return state.preferences.length > 0;
  return STEP_META[id].ready(state);
}
